import {useRef, useState} from 'react';

import {clockTime} from '../format';

import {Fullscreen, Pause, Play, Speaker} from './Icons';

/**
 * The final-cut player.
 *
 * Custom controls rather than the browser's, because the design calls for the
 * paper-and-maroon transport and a 9:16 frame — but every control drives the real
 * `<video>` element, so what is on screen is the actual exported file.
 */
export function VideoPlayer({
  src,
  aspectRatio,
  onDuration,
}: {
  src: string;
  aspectRatio: string;
  /** Reports the exported file's real duration, which the details panel prefers. */
  onDuration(seconds: number): void;
}) {
  const shell = useRef<HTMLDivElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const toggle = () => {
    const el = video.current;
    if (!el) return;
    if (el.paused) void el.play();
    else el.pause();
  };

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = video.current;
    if (!el || !Number.isFinite(el.duration)) return;
    const box = e.currentTarget.getBoundingClientRect();
    el.currentTime = ((e.clientX - box.left) / box.width) * el.duration;
  };

  return (
    <div className="player" ref={shell} style={{aspectRatio}}>
      <video
        ref={video}
        src={src}
        playsInline
        preload="metadata"
        onClick={toggle}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d)) {
            setDuration(d);
            onDuration(d);
          }
        }}
      />

      {!playing && (
        <button type="button" className="big" onClick={toggle} aria-label="تشغيل">
          <i>
            <Play size={26} fill="#7C1F2E" />
          </i>
        </button>
      )}

      <div className="pbar">
        <div className="track" onClick={seek}>
          <div style={{width: duration ? `${(time / duration) * 100}%` : 0}} />
        </div>
        <div className="row">
          <button type="button" onClick={toggle} aria-label={playing ? 'إيقاف' : 'تشغيل'}>
            {playing ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" />}
          </button>
          <button
            type="button"
            aria-label="كتم الصوت"
            onClick={() => {
              const el = video.current;
              if (!el) return;
              el.muted = !el.muted;
              setMuted(el.muted);
            }}
          >
            <Speaker muted={muted} />
          </button>
          <time>
            {clockTime(time)} / {clockTime(duration)}
          </time>
          <span className="grow" />
          <button
            type="button"
            aria-label="ملء الشاشة"
            onClick={() => void shell.current?.requestFullscreen?.()}
          >
            <Fullscreen />
          </button>
        </div>
      </div>
    </div>
  );
}
